import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  signal,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MultilingualService } from '@core/services';
import { CMSContent } from '@models/interfaces';
import { AppConstants } from '@root/app/app.constants';
import { AppCenterAnalytics, AppInsightsService, CmsService } from '@shared/services';
import { scrollToSection } from '@shared/utils';

@Component({
  selector: 'app-terms-of-use',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './terms-of-use.component.html',
  styleUrls: ['./terms-of-use.component.scss'],
  standalone: false,
})
export class TermsOfUseComponent implements OnInit, OnDestroy {
  @Input() isOpenedFromGuest!: boolean;

  readonly termsOfUse = signal<CMSContent | null>(null);
  readonly isLoading = signal(false);
  readonly isError = signal(false);
  readonly errMsg = signal<string>('');
  private readonly unlistenFns: (() => void)[] = [];

  constructor(
    private readonly cmsService: CmsService,
    public readonly multilingualService: MultilingualService,
    private readonly appInsightService: AppInsightsService,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.appInsightService.trackPageView(AppCenterAnalytics.TERMS_OF_USE_VIEWED);
    this.isLoading.set(true);
    this.isError.set(false);
    this.errMsg.set('');
    this.cmsService
      .getTermsOfUse()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: CMSContent) => {
          this.termsOfUse.set(res);
          this.isLoading.set(false);

          // Attach click listeners after content and anchors are present in the DOM.
          requestAnimationFrame(() => {
            this.attachClickEventListeners();
          });
        },

        error: (err: unknown) => {
          this.isLoading.set(false);
          this.isError.set(true);
          this.errMsg.set(String(err));
        },
      });
  }

  // Attach click event listeners after content is loaded
  private attachClickEventListeners(): void {
    this.clearClickEventListeners();

    for (const linkId of AppConstants.TERMS_OF_USE_INTERNAL_LINK_IDs) {
      const anchor = this.elRef.nativeElement.querySelector(`a[href="#${linkId}"]`);
      if (anchor) {
        const unlisten = this.renderer.listen(anchor, 'click', (event) => {
          scrollToSection(linkId);
          event.preventDefault(); // Prevent default anchor behavior (URL update)
        });
        this.unlistenFns.push(unlisten);
      }
    }
  }

  private clearClickEventListeners(): void {
    while (this.unlistenFns.length) {
      const unlisten = this.unlistenFns.pop();
      unlisten?.();
    }
  }

  ngOnDestroy(): void {
    this.clearClickEventListeners();
  }
}
